import { JOURNEY_COLOR, MONO } from './journeyTheme'
import type { JourneyKpi, JourneyKpis } from './journeys'

type CardDef = {
  kpi: JourneyKpi
  label: string
  value: number
  /** Small pill beside the number, e.g. Awaiting Results' overdue subset. */
  note?: string
  noteBg?: string
  noteColor?: string
}

/**
 * The four KPIs as a single banded strip, per the approved design: mono
 * uppercase labels, large tabular numerals, and colour reserved for the note
 * pill — so the strip reads as data rather than as four competing buttons.
 */
export const JourneyKpiCards = ({
  kpis,
  selected,
  onSelect,
}: {
  kpis: JourneyKpis
  selected: JourneyKpi | null
  onSelect: (kpi: JourneyKpi | null) => void
}) => {
  const cards: CardDef[] = [
    { kpi: 'active', label: 'Active care journeys', value: kpis.active },
    {
      kpi: 'awaiting-results',
      label: 'Awaiting results',
      value: kpis.awaitingResults,
      note: kpis.awaitingResultsOverdue > 0 ? `${kpis.awaitingResultsOverdue} overdue` : undefined,
      noteBg: '#fdf0ef',
      noteColor: JOURNEY_COLOR.late,
    },
    {
      kpi: 'results-need-followup',
      label: 'Results need follow-up',
      value: kpis.resultsNeedFollowUp,
    },
    { kpi: 'completed', label: 'Completed journeys', value: kpis.completed },
  ]

  return (
    <div
      className="grid grid-cols-1 overflow-hidden rounded-[14px] bg-white sm:grid-cols-2 xl:grid-cols-4"
      style={{ border: `1px solid ${JOURNEY_COLOR.border}` }}
    >
      {cards.map((card, index) => (
        <button
          key={card.kpi}
          type="button"
          onClick={() => onSelect(selected === card.kpi ? null : card.kpi)}
          aria-pressed={selected === card.kpi}
          className="flex flex-col gap-[7px] px-8 py-5 text-left transition-colors"
          style={{
            borderLeft: index === 0 ? undefined : `1px solid ${JOURNEY_COLOR.hairline}`,
            background: selected === card.kpi ? '#f4f7fc' : JOURNEY_COLOR.surfaceAlt,
            boxShadow: selected === card.kpi ? `inset 0 -2px 0 0 ${JOURNEY_COLOR.wait}` : undefined,
          }}
        >
          <div
            className="text-[10.5px] tracking-[0.12em] uppercase"
            style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
          >
            {card.label}
          </div>
          <div className="flex items-baseline gap-[11px]">
            <div
              className="text-[30px] leading-none font-medium tracking-[-0.02em] tabular-nums"
              style={{ color: JOURNEY_COLOR.ink }}
            >
              {card.value}
            </div>
            {card.note ? (
              <div
                className="rounded-full px-2 py-0.5 text-[12.5px] font-medium"
                style={{ background: card.noteBg, color: card.noteColor }}
              >
                {card.note}
              </div>
            ) : null}
          </div>
        </button>
      ))}
    </div>
  )
}
